import React, { useMemo, useLayoutEffect, useState } from "react"
import humanReadableDaysAgo from "../util/humanReadableTimeAgo"
import { EditorState, convertFromRaw } from "draft-js"
import positionCommentX from "../util/positionCommentX"
import positionCommentY from "../util/positionCommentY"
import ReactTooltip from "react-tooltip"
import generateReactionTooltip from "../util/generateReactionTooltip"
import type { User, Thread } from "../types"

export interface JamPreviewProps {
	thread: Thread
	onClick: (event: React.MouseEvent<HTMLElement>) => void
}

export default function JamPreview(props: JamPreviewProps) {
	let { thread } = props
	let { comments } = thread
	let lastComment = comments[comments.length - 1]

	let messagePreview = useMemo(() => {
		let jsonifiedCommentMessage = JSON.parse(lastComment.message)
		let messageConvertedFromRaw = convertFromRaw(jsonifiedCommentMessage)
		let tempEditorState = EditorState.createWithContent(messageConvertedFromRaw)
		let commentText = tempEditorState.getCurrentContent().getPlainText("\u0001")
		return commentText.replace("\u0001", " ")
	}, [lastComment])

	let authors = useMemo(() => {
		let authors = new Map<string, User>()

		for (let comment of comments) {
			if (!authors.has(comment.author.name)) {
				authors.set(comment.author.name, comment.author)
			}

			if (authors.size >= 6) {
				break
			}
		}

		return Array.from(authors.values())
	}, [comments])

	let reactions = useMemo(() => {
		let reactions: Array<{ emoji: string; upvotedUsers: string[] }> = []

		for (let comment of comments.slice(0, 3)) {
			if (comment.reactions) {
				for (let reaction of comment.reactions) {
					reactions.push({
						emoji: reaction.emoji,
						upvotedUsers: reaction.upvotedUsers,
					})
				}
			}
		}

		return reactions
	}, [comments])

	// TODO: This should all be refactored to use a ref
	let [positionedX, setPositionedX] = useState(0)
	let [positionedY, setPositionedY] = useState(0)
	useLayoutEffect(() => {
		setPositionedX(positionCommentX(thread, "thread"))
		setPositionedY(positionCommentY(thread, "thread"))
	}, [thread])

	return (
		<div
			onClick={props.onClick}
			key={`jam--preview-${thread._id}`}
			style={{
				top: `${positionedY}px`,
				left: `${positionedX}px`,
			}}
			id={`jam--preview-thread-${thread._id}`}
			className={`jam--preview ${thread._id}`}
		>
			<div className="jam--preview-conainer-outer">
				<div className="jam--preview-avatars">
					{authors.map((author) => {
						return <img key={author._id} src={author.avatar} />
					})}
				</div>
				<div className="jam--preview-container">
					<div className="jam--preview-container-row">
						<p className="jam--preview-text">{messagePreview}</p>
						<div className="jam--preview-reply-date">
							{humanReadableDaysAgo(lastComment._id)}
						</div>
					</div>
					<div className="jam--preview-container-row">
						{reactions.length > 0 && (
							<div className="jam--preview-reactions">
								{reactions.map((reaction, index) => {
									const tooltipText = generateReactionTooltip(
										reaction.emoji,
										reaction.upvotedUsers,
									)
									return (
										<div
											key={`emoji-${index}`}
											className="jam--preview-reaction"
											data-tip={tooltipText}
										>
											{reaction.emoji}
										</div>
									)
								})}
							</div>
						)}
					</div>
					<ReactTooltip
						place="top"
						type="dark"
						effect="float"
						// condition={false}
					/>
				</div>
			</div>
			<style>{`
				.jam--preview {
					max-width: 400px;
					height: 66px;
					background: #fff;
					box-sizing: border-box;
					display: flex;
					flex-direction: row;
					align-items: center;
					justify-content: space-around;
					border-radius: 10px;
					box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
					font-family: "SF Pro Text",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
					Helvetica, Arial, sans-serif;
					color: #2A3632;
					position: absolute;
					font-size: 14px;
					z-index: 1000000;
					padding: 5px 10px;
				}

				@media screen and (max-width: 450px) {
					.jam--preview {
						width: 365px;
						margin-left: auto;
						margin-right: auto;
						left: 0 !important;
						right: 0;
					}
				}

				.jam--preview-conainer-outer {
					display: flex;
					flex-direction: row;
					width: 100%;
					align-items: center;
					z-index: 1000000;
				}

				.jam--preview-container-row {
					align-items: center;
					display: flex;
					flex-direction: row;
					width: 100%;
					font-size: 14px;
					overflow: hidden;
					z-index: 1000000;
				}

				.jam--preview-container {
					display: flex;
					flex-direction: column;
					max-width: 310px;
					z-index: 1000000;
				}

				.jam--preview-container:hover {
					cursor: pointer;
				}

				.jam--preview-avatars {
					display: flex;
					flex-direction: row;
					align-items: center;
					margin-right: 4px;
				}

				.jam--preview-avatars img {
					border-radius: 50%;
					height: 40px;
					width: 40px;
					box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.05);
				}

				.jam--preview-avatars img:not(:first-child) {
					margin-left: -13px;
				}

				.jam--preview-num-comments {
					font-weight: 400;
					color: #142dbd;
					font-size: 14px;
					margin-right: 4px;
					white-space: nowrap;
					line-height: 20px;
				}

				.jam--preview-reply-date {
					color: rgba(42, 54, 50, .5);
					font-size: 14px;
					white-space: nowrap;
					line-height: 20px;
					font-weight: 400;
				}

				.jam--preview-text {
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					margin: initial;
					margin-right: 4px;
					margin-left: 4px;
					line-height: 20px;
					font-size: 14px;
					color: #3C81C7;
					background: rgba(100, 175, 250, .15);
				}

				.jam--preview-notification {
					align-self: center;
					border-radius: 50%;
					background: #e8180a;
					height: 8px;
					width: 8px;
					font-size: 14px;
				}

				#jam--eye-icon-toggle-preview {
					margin-right: 6px;
					margin-left: 2px;
					opacity: 0.5;
				}

				.jam--preview-reactions {
					display: flex;
					flex-direction: row;
					align-items: center;
					padding: 6px;
					margin: 0 4px 0 0;
					justify-content: center;
					box-sizing: border-box;
					margin-top: 5px;
					height: 12px;
				}

				.jam--preview-reaction {
					margin-top: 1em;
					margin-bottom: 1em;
					font-size: 10px;
				}
			`}</style>
		</div>
	)
}
